// Force `crossorigin="anonymous"` on every dynamically-injected <script> so
// uncaught errors from third-party loaders (Google Maps, etc.) surface real
// messages/stacks in window.onerror instead of the opaque "Script error."
// that browsers emit for CORS-isolated scripts. Must run before any module
// creates a script element. Static ES imports above this file don't inject
// scripts at import time, so installing the override at the top of main.tsx
// is early enough for runtime-injected scripts (Maps loader, etc.).
// Hosts that serve scripts WITHOUT an `Access-Control-Allow-Origin` header.
// Forcing `crossorigin="anonymous"` on these makes the browser fetch them in
// CORS mode and then reject the (otherwise 200 OK) response for lack of ACAO —
// which silently breaks the loader. Google Maps' bootstrap injects its versioned
// `main.js` chunk this way, so it MUST be excluded or the map never loads.
const NO_CORS_SCRIPT_HOSTS =
  /(^|\.)(googleapis|gstatic|google)\.com$/i;

const _origCreateElement = document.createElement.bind(document);
document.createElement = function (
  this: Document,
  tagName: string,
  options?: ElementCreationOptions,
) {
  const el = _origCreateElement(tagName as any, options);
  if (typeof tagName === "string" && tagName.toLowerCase() === "script") {
    const script = el as HTMLScriptElement;
    // `src` isn't known at creation time (loaders set it afterward), so decide
    // whether to tag `crossorigin` based on the URL when `src` is assigned.
    let decided = false;
    const decide = (url?: string | null) => {
      if (decided) return;
      decided = true;
      try {
        if (url) {
          const host = new URL(url, window.location.href).hostname;
          if (NO_CORS_SCRIPT_HOSTS.test(host)) return; // leave crossorigin unset
        }
      } catch {
        /* fall through and tag it */
      }
      script.crossOrigin = "anonymous";
    };
    try {
      const srcDesc = Object.getOwnPropertyDescriptor(
        HTMLScriptElement.prototype,
        "src",
      );
      if (srcDesc && srcDesc.get && srcDesc.set) {
        Object.defineProperty(script, "src", {
          configurable: true,
          enumerable: true,
          get() {
            return srcDesc.get!.call(this);
          },
          set(value: string) {
            decide(value);
            srcDesc.set!.call(this, value);
          },
        });
      }
      const _setAttribute = script.setAttribute.bind(script);
      script.setAttribute = function (name: string, value: string) {
        if (name.toLowerCase() === "src") decide(value);
        return _setAttribute(name, value);
      };
    } catch {
      // If interception fails for any reason, fall back to the original
      // behavior so script creation itself never throws.
      script.crossOrigin = "anonymous";
    }
  }
  return el;
} as typeof document.createElement;

import { createRoot } from "react-dom/client";
import { PostHogProvider } from "@posthog/react";
import { POSTHOG_KEY, POSTHOG_HOST } from "./lib/analytics";
import App from "./App";
import "./index.css";
import "./styles/tork-seo.css";

// Note: manual window.error / window.unhandledrejection listeners were
// removed — PostHog's SDK does this natively via `capture_exceptions: true`
// below. The manual handlers were also emitting empty-message Error reports
// on Mobile Safari for non-Error promise rejections (AbortController.abort,
// canceled fetches, etc.).

const posthogOptions = {
  api_host: POSTHOG_HOST,
  autocapture: true,
  capture_pageview: true,
  capture_pageleave: true,
  enable_heatmaps: true,
  enable_recording: true,
  capture_exceptions: true,
  // Drop residual CORS-masked "Script error." reports that slip past the
  // crossorigin override (some third-party loaders set src before we can
  // re-tag the element, etc.). These reports have no stack and no message
  // we can act on, so they're pure noise in PostHog.
  before_send: (event: any) => {
    if (!event) return event;
    const msg = event.properties?.$exception_message;
    if (msg === "Script error." || msg === "Script error") return null;
    return event;
  },
  session_recording: {
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
    },
  },
  loaded: (ph: any) => {
    if (import.meta.env.DEV) ph.debug()
  },
};

createRoot(document.getElementById("root")!).render(
  POSTHOG_KEY ? (
    <PostHogProvider apiKey={POSTHOG_KEY} options={posthogOptions}>
      <App />
    </PostHogProvider>
  ) : (
    <App />
  )
);
